import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildTherapyHref, getActivePromoEvents, PromoEvent } from '../../data/events';
import './booking.css';

declare global {
    interface Window {
        Calendly?: {
            initPopupWidget: (options: { url: string }) => void;
        };
    }
}

const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_CSS = 'https://assets.calendly.com/assets/external/widget.css';
const MOBILE_USER_AGENT = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

interface BookingOption {
    id: string;
    date: { day: string; month: string } | null;
    badge: string;
    title: string;
    subtitle: string;
    desc: string;
    url: string;
    cta: string;
    therapyLink: string | null;
}

const ALL_EVENTS: BookingOption[] = [
    {
        id: 'behandlung',
        date: null,
        badge: 'Ersttermin oder Folgebehandlung',
        title: 'Behandlung',
        subtitle: 'Standort Regensburg · Ziegetsdorfer Str. 32',
        desc: 'Ganzheitliche Naturheilkunde, individuell auf Sie abgestimmt.',
        url: 'https://calendly.com/hploewe/30min?primary_color=409443',
        cta: 'Termin buchen',
        therapyLink: null,
    },
    {
        id: 'behandlung-kelheim',
        date: null,
        badge: 'Ersttermin oder Folgebehandlung',
        title: 'Behandlung',
        subtitle: 'Standort Kelheim · Donaustr. 14',
        desc: 'Ganzheitliche Naturheilkunde, individuell auf Sie abgestimmt.',
        url: 'https://calendly.com/hploewe/behandlung-kelheim-donaustr',
        cta: 'Termin buchen',
        therapyLink: null,
    },
    {
        id: 'behandlung-regensburg-maximilianstr',
        date: null,
        badge: 'Ersttermin oder Folgebehandlung',
        title: 'Behandlung',
        subtitle: 'Standort Regensburg · Maximilianstr. 24',
        desc: 'Ganzheitliche Naturheilkunde, individuell auf Sie abgestimmt.',
        url: 'https://calendly.com/hploewe/behandlung-regensburg-maximilianstr',
        cta: 'Termin buchen',
        therapyLink: null,
    },
];
const EVENTS = ALL_EVENTS;
const ACTIVE_PROMO_EVENTS = getActivePromoEvents();

const Booking = () => {
    const [ready, setReady] = useState(!!window.Calendly);
    const [isMobileClient, setIsMobileClient] = useState(false);

    useEffect(() => {
        setIsMobileClient(MOBILE_USER_AGENT.test(window.navigator.userAgent));

        if (!document.querySelector(`link[href="${CALENDLY_CSS}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = CALENDLY_CSS;
            document.head.appendChild(link);
        }
        if (window.Calendly) { setReady(true); return; }
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${CALENDLY_SCRIPT}"]`);
        if (existing) {
            existing.addEventListener('load', () => setReady(true), { once: true });
            return;
        }
        const script = document.createElement('script');
        script.src = CALENDLY_SCRIPT;
        script.async = true;
        script.addEventListener('load', () => setReady(true), { once: true });
        document.body.appendChild(script);
    }, []);

    const openPopup = (e: React.MouseEvent, url: string) => {
        e.preventDefault();
        window.Calendly?.initPopupWidget({ url });
    };

    const renderPromoCta = (event: PromoEvent) => {
        const { action } = event;

        if (action.type === 'calendly') {
            return (
                <button
                    className="buttonPrimary"
                    onClick={e => openPopup(e, action.url)}
                    disabled={!ready}
                >
                    {event.ctaLabel}
                </button>
            );
        }

        if (action.href.startsWith('http')) {
            return (
                <a
                    className="buttonSecondary"
                    href={action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {event.ctaLabel}
                </a>
            );
        }

        return (
            isMobileClient ?
                <a className="buttonSecondary" href="tel:+491776962490">
                    Rufen Sie mich an
                </a>
                :
                <a className="buttonSecondary" href="/#page-contact">
                    Kontaktieren Sie mich
                </a>
        );
    };

    return (
        <div className="section sectionBackground" id="booking">
            <div className="viewport">
                <div className="bookingItem">
                    <h1>Termin buchen</h1>
                    <p className="sectionLead">
                        Buchen Sie hier Ihren Termin. Wenn Sie vorab kurz Ihr Anliegen schildern m&ouml;chten,
                        rufen Sie gerne an oder schreiben Sie eine E&#8209;Mail.
                    </p>
                    <div className="bookingCards">
                        {EVENTS.map(ev => (
                            <div key={ev.id} className="bookingCard">
                                <div className="bookingCardHeader">
                                    <span className="bookingCardBadge">{ev.badge}</span>
                                    {ev.date && (
                                        <div className="bookingCardDate" aria-label={`${ev.date.day}. ${ev.date.month}`}>
                                            <span className="bookingCardDay">{ev.date.day}</span>
                                            <span className="bookingCardMonth">{ev.date.month}</span>
                                        </div>
                                    )}
                                </div>
                                <h2 className="bookingCardTitle">{ev.title}</h2>
                                <p className="bookingCardSubtitle">{ev.subtitle}</p>
                                <p className="bookingCardDesc">{ev.desc}</p>
                                {ev.therapyLink && (
                                    <a className="bookingCardTherapyLink" href={ev.therapyLink}>
                                        Mehr zur SANZA&#8209;Methode&nbsp;→
                                    </a>
                                )}
                                <button
                                    className={ev.id === 'behandlung' ? 'buttonPrimary' : 'buttonSecondary'}
                                    onClick={e => openPopup(e, ev.url)}
                                    disabled={!ready}
                                >
                                    {ev.cta}
                                </button>
                            </div>
                        ))}
                    </div>
                    {ACTIVE_PROMO_EVENTS.length > 0 && (
                        <div className="bookingEventsSection">
                            <h2 className="bookingEventsTitle">Aktuelle Events &amp; Aktionen</h2>
                            <p className="bookingEventsLead">
                                Diese aktuell verf&uuml;gbaren Angebote und Veranstaltungen k&ouml;nnen Sie direkt buchen,
                                online reservieren oder telefonisch anfragen.
                            </p>
                            <div className="bookingCards">
                                {ACTIVE_PROMO_EVENTS.map((event) => (
                                    <div key={event.id} className="bookingCard bookingCardHighlight">
                                        <div className="bookingCardHeader">
                                            <span className="bookingCardBadge">{event.badge}</span>
                                            {event.day && event.month && (
                                                <div className="bookingCardDate" aria-label={`${event.day}. ${event.month}`}>
                                                    <span className="bookingCardDay">{event.day}</span>
                                                    <span className="bookingCardMonth">{event.month}</span>
                                                </div>
                                            )}
                                        </div>
                                        <h3 className="bookingCardTitle">{event.title}</h3>
                                        <p className="bookingCardSubtitle">
                                            {event.time ? `${event.time} · ` : ''}Standort Regensburg
                                        </p>
                                        <p className="bookingCardDesc">{event.desc}</p>
                                        {event.moreTherapyId && (
                                            <a className="bookingCardTherapyLink" href={buildTherapyHref(event.moreTherapyId)}>
                                                Mehr zur SANZA-Methode&nbsp;→
                                            </a>
                                        )}
                                        {renderPromoCta(event)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="bookingNote">
                        Die Buchung erfolgt &uuml;ber Calendly (externer Dienst). Hinweise zur Datenverarbeitung finden Sie in der{' '}
                        <Link to="/privacy">Datenschutzerkl&auml;rung</Link>.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Booking;
